/**
 * "Crew" card on the project page (A2, D-3): who is assigned to the PROJECT
 * directly, no task needed — the lead, each member's committed days, and (for
 * supervisors/admins) the Assign-crew picker. This is the primary assignment
 * now; per-task crew (the optional checklist) still shows on each task row.
 */
import { ClipboardList, Star } from 'lucide-react';
import type { ScheduleDay } from '@vari-crew/schemas';
import { useProjectAssignments } from '@/api/project-assignments';
import { Avatar } from '@/components/ui/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AssignProjectCrewButton } from './AssignProjectCrewButton';

export function ProjectCrewCard({
  project,
  canManage,
  readOnlyReason,
}: {
  project: { id: string; title: string; approved_schedule: ScheduleDay[] | null };
  /** Supervisor/admin — shows the picker. */
  canManage: boolean;
  /** When set (complete / cancelled), the picker is hidden even for managers. */
  readOnlyReason?: string | null;
}) {
  const { data, isLoading } = useProjectAssignments(project.id);
  const crew = data?.assignments ?? [];
  const showPicker = canManage && !readOnlyReason;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Crew</CardTitle>
        {showPicker && <AssignProjectCrewButton project={project} />}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-xs text-ink-faint">Loading crew…</div>
        ) : crew.length === 0 ? (
          <div className="text-sm text-ink-dim">
            No crew assigned yet.
            {showPicker && ' Assign crew to put this project on their Today.'}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {crew.map((c) => {
              const scheduledDays = c.blocks.filter((b) => b.scheduled_start).length;
              return (
                <li key={c.crew_member_id} className="flex items-center gap-2 min-w-0">
                  <Avatar name={c.display_name} src={c.avatar_url} className="w-7 h-7 text-[11px]" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate flex items-center gap-1">
                      <span className="truncate">{c.display_name}</span>
                      {c.is_lead && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium shrink-0"
                          title="Crew lead"
                        >
                          <Star className="w-3 h-3 fill-amber-400" /> lead
                        </span>
                      )}
                      {/* Inherited from a checklist step — not project-assigned;
                          their assignment lives on the step (Ben 2026-08-19). */}
                      {c.via_tasks && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full bg-fill px-1.5 py-0.5 text-[10px] text-ink-dim shrink-0"
                          title="Assigned on a checklist step, not the project — manage it on the step"
                        >
                          <ClipboardList className="w-3 h-3" /> via checklist
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-ink-faint truncate">
                      {c.crew_role}
                      {' · '}
                      {c.via_tasks
                        ? `${c.blocks.length} checklist step${c.blocks.length === 1 ? '' : 's'}`
                        : scheduledDays > 0
                          ? `${scheduledDays} committed ${scheduledDays === 1 ? 'day' : 'days'}`
                          : 'awaiting committed dates'}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
